import { useState } from "react";
import { Check } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * The three ways an answer is given in the Sprendimų laboratorija, and the one
 * rule they share: **none of these is a test.**
 *
 * There is no right option and no score anywhere in this section — the options
 * are different defensible readings of the same story, so nothing is ever drawn
 * as correct or wrong.
 *
 * What changes with age is what a tap is *for*. At seven it is scaffolding —
 * the thought is there and the handwriting is not. At fourteen it is the
 * position, and the writing is spent defending it against somebody who took the
 * other side. See `Situations::Problems::MIX_TABLE`.
 */
export type SituationQuestion = {
  key: string;
  prompt: string;
  answerType: string;
  options: string[];
  asksWhy: boolean;
  counter?: string | null;
  allowsOther?: boolean;
  lines: number;
  answer: string | null;
  reasonAnswer?: string | null;
  counterAnswer?: string | null;
};

export const OPEN = "open";
export const CHOICE = "choice";
export const MULTI = "multi";
export const WEIGH = "weigh";

/**
 * „Weigh" is the one format with nothing to be right about: every road gets a box
 * for what it would cost, and none of them is picked. It is what „ar taip galima"
 * is actually made of — a question phrased as a verdict has a correct answer
 * implied in it whatever its options say.
 *
 * The notes live under the question's own key, one line per road:
 *
 *   Užleisti spausdintuvą → Gintarė spėtų, bet Kajus rizikuoja
 *
 * Matched back to the road by its **words**, never by position — the same rule a
 * pick follows, because the options get reworded around the learner's world and a
 * note filed under „the second one" would then describe something else.
 */
export const WEIGH_SEPARATOR = " → ";
export const WEIGH_PROMPT = "Kas nuo to pasikeistų? Kam tai kainuotų?";

export function splitWeighed(
  answer: string | null | undefined,
  options: string[]
): { notes: Record<string, string>; leftover: string[] } {
  const notes: Record<string, string> = {};
  const leftover: string[] = [];

  (answer ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const option = options.find((o) => line.startsWith(o + WEIGH_SEPARATOR));
      if (option) {
        notes[option] = line.slice(option.length + WEIGH_SEPARATOR.length).trim();
      } else {
        // A note whose road is no longer on offer. Shown rather than dropped: the
        // child wrote it, and silently emptying the box reads as the app losing it.
        leftover.push(line);
      }
    });

  return { notes, leftover };
}

export function joinWeighed(notes: Record<string, string>, options: string[]): string {
  return options
    .filter((option) => (notes[option] ?? "").trim().length > 0)
    .map((option) => `${option}${WEIGH_SEPARATOR}${notes[option].trim()}`)
    .join("\n");
}

/** Fixed wording, so it is one thing to learn rather than one per question. */
export const WHY_LABEL = "Kodėl taip pasirinkai?";

/**
 * The peer who disagreed, and the row for an answer that is not on the list.
 * Both mirror `Situations::Problems` — they are the app speaking rather than the
 * story, so they are written once here and not per file.
 *
 * Neither names a gender: „ką jam atsakytum?" would invent a boy for an argument
 * nobody attributed.
 */
export const COUNTER_LEAD = "Kai kas atsakė kitaip:";
export const COUNTER_PROMPT = "Ką atsakytum?";
export const OTHER_LABEL = "Kita";

/**
 * A write-in is an option the child wrote, so it is bounded like one
 * (`Catalog::MAX_OPTION_CHARS`) and it is **one line** — a single-line field, not
 * a textarea. Picks are stored one per line, so a newline inside a write-in
 * would come back as two separate answers.
 */
export const MAX_OTHER_CHARS = 160;

/**
 * A pick is stored as the **words** of the option, not as its position — so it
 * is still readable years later, after the telling has been rewritten and after
 * the child has grown into the next one. Several picks go one per line, which is
 * the one separator an option can never itself contain.
 */
export function splitPicks(answer: string | null | undefined): string[] {
  return (answer ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

export function joinPicks(picks: string[]): string {
  return picks.join("\n");
}

/** The key the „Kodėl?" box is stored under. Mirrors `Problems::REASON_SUFFIX`. */
export function reasonKey(questionKey: string): string {
  return `${questionKey}_kodel`;
}

/** Where the answer to the peer's argument goes. Mirrors `Problems::COUNTER_SUFFIX`. */
export function counterKey(questionKey: string): string {
  return `${questionKey}_atsakymas`;
}

/**
 * Every key one question stores something under, in the order the boxes appear.
 * Mirrors `Catalog::Question#answer_keys`, which is what the server accepts —
 * the two lists have to agree or a box saves into nothing.
 */
export function answerKeysOf(question: SituationQuestion): string[] {
  return [
    question.key,
    ...(question.asksWhy ? [reasonKey(question.key)] : []),
    ...(question.counter ? [counterKey(question.key)] : []),
  ];
}

/**
 * What the child wrote before, when it matches none of the options on offer —
 * an answer given when this question was still an open one, or under a telling
 * since rewritten. Shown rather than dropped: it is theirs, and silently
 * emptying the box would read as the app losing it.
 */
function Leftover({ text }: { text: string }) {
  return (
    <p className="rounded-md bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
      Anksčiau parašei: <span className="text-foreground">{text}</span>
    </p>
  );
}

function OptionRow({
  label,
  picked,
  multi,
  onClick,
}: {
  label: string;
  picked: boolean;
  multi: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={picked}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition",
        "hover:border-primary/60 hover:bg-primary/5",
        picked ? "border-primary bg-primary/10" : "border-input"
      )}
    >
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center border",
          // Round for "pick one", square for "pick as many as fit" — the shape
          // is what says how many are allowed, before anything is tapped.
          multi ? "rounded-[0.3rem]" : "rounded-full",
          picked ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/50"
        )}
      >
        {picked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      </span>
      <span className="text-[1.02rem] leading-snug">{label}</span>
    </button>
  );
}

/**
 * Somebody else's answer, and a box to answer it in.
 *
 * This is the one place an older telling asks for real writing, and it is
 * deliberately the easiest place to start writing: answering a person is not the
 * same task as filling a box. It is also the hardest place to give the answer a
 * grown-up wants to hear, because that answer is already on the screen as the
 * thing being disagreed with.
 *
 * Drawn as a quotation rather than as a prompt — it is a claim, not the app
 * asking something, and a child who cannot tell those apart will read it as the
 * correct answer.
 */
function CounterArgument({
  text,
  value,
  onChange,
}: {
  text: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-dashed bg-muted/40 p-3">
      <p className="text-sm text-muted-foreground">{COUNTER_LEAD}</p>
      <blockquote className="border-l-2 border-muted-foreground/40 pl-3 text-[1.02rem] italic leading-snug">
        {text}
      </blockquote>
      <label className="block pt-0.5 text-sm font-medium">{COUNTER_PROMPT}</label>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder="Rašyk čia…"
        className="bg-background text-[1.02rem] leading-relaxed"
      />
    </div>
  );
}

/**
 * One question's answer field, whichever shape it is. The value is always a
 * string, so everything above this — drafts, saving, printing, the grown-up's
 * review — treats a tap and a sentence the same way.
 */
export function AnswerField({
  question,
  value,
  reason,
  counterAnswer,
  onChange,
  onReasonChange,
  onCounterChange,
}: {
  question: SituationQuestion;
  value: string;
  reason: string;
  counterAnswer: string;
  onChange: (next: string) => void;
  onReasonChange: (next: string) => void;
  onCounterChange: (next: string) => void;
}) {
  const multi = question.answerType === MULTI;
  const closed = multi || question.answerType === CHOICE;
  const weigh = question.answerType === WEIGH;
  const allowsOther = closed && question.allowsOther === true;

  const picks = splitPicks(value);
  const chosen = picks.filter((pick) => question.options.includes(pick));
  const unlisted = picks.filter((pick) => !question.options.includes(pick));

  // What they typed into „Kita", which is simply the part of the answer that
  // matches no option — so there is no extra key and no flag to keep in sync,
  // and a grown-up's sheet reads their words rather than the word „Kita".
  const written = allowsOther ? unlisted.join(" ") : "";
  const [otherOpen, setOtherOpen] = useState(false);
  const otherActive = written.length > 0 || otherOpen;

  const counter = question.counter?.trim();
  const counterBlock = counter ? (
    <CounterArgument text={counter} value={counterAnswer} onChange={onCounterChange} />
  ) : null;

  if (weigh) {
    const { notes, leftover } = splitWeighed(value, question.options);

    return (
      <div className="space-y-3">
        {/* Said once, above: there is nothing to choose here, and a child who
            expects to pick will look for the right one. */}
        <p className="text-sm text-muted-foreground">
          Nieko rinktis nereikia — prie kiekvieno kelio parašyk, kas nuo to
          pasikeistų.
        </p>
        {question.options.map((option) => (
          <div key={option} className="rounded-lg border p-3">
            <p className="text-[1.02rem] font-medium leading-snug">{option}</p>
            <label className="mt-1.5 block text-sm text-muted-foreground">
              {WEIGH_PROMPT}
            </label>
            <Textarea
              value={notes[option] ?? ""}
              onChange={(e) =>
                onChange(
                  joinWeighed({ ...notes, [option]: e.target.value }, question.options)
                )
              }
              rows={2}
              placeholder="Rašyk čia…"
              className="mt-1 text-[1.02rem] leading-relaxed"
            />
          </div>
        ))}
        {leftover.length > 0 && <Leftover text={leftover.join(" · ")} />}
        {counterBlock}
      </div>
    );
  }

  if (!closed) {
    return (
      <div className="space-y-3">
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={question.lines}
          placeholder="Rašyk čia…"
          className="text-[1.05rem] leading-relaxed"
        />
        {counterBlock}
      </div>
    );
  }

  const commit = (nextChosen: string[], nextWritten: string) => {
    const trailing = allowsOther ? [nextWritten.trim()] : unlisted;
    onChange(joinPicks([...nextChosen, ...trailing].filter(Boolean)));
  };

  const toggle = (option: string) => {
    if (!multi) {
      // Tapping the chosen one again clears it. Nothing here is compulsory, and
      // a child who changed their mind to "actually, none of these" has no other
      // way of saying so. Picking one also puts „Kita" away — one answer means
      // one answer.
      const next = chosen.includes(option) ? "" : option;
      setOtherOpen(false);
      onChange(next);
      return;
    }
    commit(
      chosen.includes(option) ? chosen.filter((pick) => pick !== option) : [...chosen, option],
      written
    );
  };

  return (
    <div className="space-y-2">
      {multi && (
        <p className="text-sm text-muted-foreground">Gali pažymėti kelis.</p>
      )}
      <div className="space-y-2">
        {question.options.map((option) => (
          <OptionRow
            key={option}
            label={option}
            picked={chosen.includes(option)}
            multi={multi}
            onClick={() => toggle(option)}
          />
        ))}

        {/* Last, and it does not open focused. „Kita" is the way out for a child
            whose answer is not on the list — not the default for one who would
            rather type nothing, which is what an autofocused box invites. */}
        {allowsOther && (
          <div className="space-y-2">
            <OptionRow
              label={OTHER_LABEL}
              picked={otherActive}
              multi={multi}
              onClick={() => {
                if (otherActive) {
                  setOtherOpen(false);
                  commit(chosen, "");
                  return;
                }
                setOtherOpen(true);
                if (!multi) onChange("");
              }}
            />
            {otherActive && (
              <Input
                value={written}
                maxLength={MAX_OTHER_CHARS}
                onChange={(e) => commit(multi ? chosen : [], e.target.value)}
                placeholder="Parašyk savo atsakymą…"
                className="text-[1.02rem]"
              />
            )}
          </div>
        )}
      </div>

      {!allowsOther && unlisted.length > 0 && <Leftover text={unlisted.join(" · ")} />}

      {question.asksWhy && (
        <div className="space-y-1.5 pt-1">
          <label className="text-sm text-muted-foreground">{WHY_LABEL}</label>
          <Textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            rows={2}
            placeholder="Rašyk čia…"
            className="text-[1.02rem] leading-relaxed"
          />
        </div>
      )}

      {counterBlock}
    </div>
  );
}
